import type CMSConnector from "./connector";
import { CMSRestError } from "./rest/errors";
import type { AccountTokenInfo, CampaignAccount } from "./rest/types";
import apple from "./services/apple";
import spotify from "./services/spotify";
import TaskLoop from "./taskloop";
import { type StreamingService } from "./types";

class AccountHandler {
  cms: CMSConnector;
  tokenInfo: AccountTokenInfo | null = null;
  refreshTimeout: number | null = null;
  refreshRunning = false;
  currentAccount: CampaignAccount | null = null;
  refreshLoop: TaskLoop;

  constructor(cms: CMSConnector) {
    this.cms = cms;
    const refreshInterval = 10 * 60 * 1000;
    this.refreshLoop = new TaskLoop(
      this.refreshToken.bind(this),
      refreshInterval
    );
  }

  async init(): Promise<void> {
    await this.loadStorageToken();
    const newLogin = await this.oauth2Callback();
    const promises: Promise<any>[] = [];
    if (this.cms.campaign.enabled_services.includes("apple")) {
      promises.push(this.initApple());
    }
    if (this.cms.campaign.enabled_services.includes("spotify")) {
      promises.push(this.initSpotify());
    }
    if (this.tokenInfo) {
      promises.push(this.loadCurrentAccount());
    }
    await Promise.all(promises);
    if (newLogin && this.currentAccount) {
      this.cms.tracking.trackCustomer("login_success", {
        method: this.currentAccount.service,
        email: this.currentAccount.email || "",
      });
    }
  }

  private async initApple() {
    await apple.init();
    if (this.tokenInfo) {
      if (this.tokenInfo.service === "apple") {
        if (!apple.isAuthorized) {
          this.clearToken();
        }
      }
    } else {
      if (apple.isAuthorized) {
        // Try to resolve the apple login to account
        await this.loginApple(true);
      }
    }
  }

  private async initSpotify() {
    await spotify.init({
      getToken: () => this.tokenInfo?.service_token || "",
    });
  }

  private setAccount(value: CampaignAccount | null) {
    this.currentAccount = value;
    this.cms.tracking.setTrackingUser(
      // TODO: May be better to pull this info from tracking wrapper
      value ? { email: value.email, logged_in: true } : null
    );
    document.dispatchEvent(
      new CustomEvent("umg:currentAccountChanged", {
        detail: this.currentAccount,
      })
    );
  }

  async loadCurrentAccount(): Promise<CampaignAccount | null> {
    if (!this.tokenInfo) {
      return null;
    }
    const value = await this.cms.api.accounts.me(this.tokenInfo.access_token);
    this.setAccount(value);
    return value;
  }

  get storageKey(): string {
    return `ACCOUNT_TOKEN:${this.cms.campaign.id}`;
  }

  setToken(tokenInfo: AccountTokenInfo) {
    // Update token in localstorage and on instance
    localStorage.setItem(this.storageKey, JSON.stringify(tokenInfo));
    this.tokenInfo = tokenInfo;
    this.refreshLoop.start();
  }

  async loadStorageToken() {
    const item = localStorage.getItem(this.storageKey);
    if (!item) {
      return;
    }
    const info: AccountTokenInfo = JSON.parse(item);
    this.tokenInfo = info;
    // Always refresh on startup
    await this.refreshToken();
  }

  clearToken() {
    this.tokenInfo = null;
    localStorage.removeItem(this.storageKey);
    this.refreshLoop.stop();
    this.setAccount(null);
  }

  async logout() {
    try {
      if (this.tokenInfo) {
        await this.cms.api.accounts.logout(this.tokenInfo.refresh_token);
      }
      await apple.unauthorize();
    } finally {
      this.clearToken();
    }
  }

  async refreshToken(): Promise<boolean> {
    console.debug("Refresh", this.tokenInfo);
    if (!this.tokenInfo) {
      return false;
    }
    let newTokenInfo: AccountTokenInfo;
    try {
      newTokenInfo = await this.cms.api.accounts.refreshLogin(
        this.tokenInfo.refresh_token
      );
    } catch (error) {
      if (error instanceof CMSRestError && error.status === 401) {
        // TODO: Logout event?
        this.clearToken();
        return false;
      } else {
        throw error;
      }
    }
    this.setToken({
      ...this.tokenInfo,
      ...newTokenInfo,
    });
    return true;
  }

  stopRefreshLoop() {
    if (this.refreshTimeout) {
      window.clearTimeout(this.refreshTimeout);
    }
    this.refreshRunning = false;
  }

  _clearCallbackParams() {
    // Remove Callback params from URL
    const url = new URL(window.location.href);
    const params = new URLSearchParams(window.location.search);
    params.delete("access_token");
    params.delete("state");
    params.delete("refresh_token");
    params.delete("service_expires_at");
    params.delete("service_token");
    params.delete("account_id");
    params.delete("auth_service");
    params.delete("error");
    url.search = params.toString();
    window.history.replaceState({}, document.title, url.toString());
  }

  async oauth2Callback(): Promise<boolean> {
    const params = new URLSearchParams(window.location.search);
    const state: string | null = params.get("state");
    if (state !== "cms_account_auth") {
      // Verify to not break own login in apps
      return false;
    }

    const access_token: string | null = params.get("access_token");
    const service: StreamingService = params.get(
      "auth_service"
    ) as StreamingService;
    const error: string | null = params.get("error");
    if (error) {
      this.cms.tracking.trackCustomer("login_fail", {
        method: service,
        error_message: error,
      });
    }
    if (!access_token || error) {
      this._clearCallbackParams();
      return false;
    }
    const refresh_token: string = params.get("refresh_token") as string;
    const service_token: string = params.get("service_token") as string;
    const service_expires_raw: string | null = params.get("service_expires_at");
    const service_expires_at: number | null = service_expires_raw
      ? parseInt(service_expires_raw)
      : null;

    this.setToken({
      access_token,
      refresh_token,
      service_token,
      service_expires_at,
      service,
    });
    this._clearCallbackParams();
    return true;
  }

  loginSpotify(opts: { redirectUri?: string } = {}): void {
    if (!this.cms.campaign.id) {
      throw new Error("CMS Campaign ID is not loaded, check meta tags");
    }
    const redirectUri = opts.redirectUri || window.location.href;
    window.location.href = this.cms.api.accounts.loginUrl(
      this.cms.campaign.id,
      "spotify",
      {
        redirect_uri: redirectUri,
        hash_output: false,
        state: "cms_account_auth",
      }
    );
  }

  async loginApple(ignoreError = false) {
    if (!this.cms.campaign.id) {
      throw new Error("CMS Campaign ID is not loaded, check meta tags");
    }
    await apple.init(); // TODO: Am I loading sdk twice?
    let token: string | null = null;
    try {
      token = await apple.authorize();
    } catch (error) {
      console.warn("Apple login fail", error);
      if (ignoreError) {
        await apple.unauthorize();
        return;
      } else {
        this.cms.tracking.trackCustomer("login_fail", {
          method: "apple",
          error_message: (error as Error).message,
        });
        throw error;
      }
    }
    let tokenInfo: AccountTokenInfo | null = null;
    try {
      tokenInfo = await this.cms.api.accounts.loginApple(
        this.cms.campaign.id,
        token
      );
    } catch (error) {
      if (!ignoreError) {
        this.cms.tracking.trackCustomer("login_fail", {
          method: "apple",
          error_message: (error as Error).message,
        });
      }
      if (
        ignoreError &&
        error instanceof CMSRestError &&
        error.status === 401
      ) {
        await apple.unauthorize();
        return;
      }
      throw error;
    }
    this.setToken(tokenInfo);
    await this.loadCurrentAccount();
  }
}

export default AccountHandler;
